"use client";

import { useMemo } from "react";
import { toSpecFieldType } from "@/lib/specs/is-specs";
import type {
  SpecFieldConfig,
  SpecFieldDefinition,
} from "../types";
import {
  PRODUCT_TYPE_CONDITIONS,
  isProductType,
  type RfqFlow,
} from "@/lib/generated/product-types";
import { PRODUCT_TYPE_FIELDS } from "@/lib/generated/product-type-fields";

interface UseFieldConfigResult {
  fieldConfig: SpecFieldConfig | null;
  isLoading: boolean;
  getField: (name: string) => SpecFieldDefinition | undefined;
  getRequiredFields: () => SpecFieldDefinition[];
  getFieldsForFlow: () => SpecFieldDefinition[];
  getConditionOptions: () => Array<{ label: string; value: string }>;
  isFieldRequiredForFlow: (fieldName: string) => boolean;
}

function fieldsForFlow(
  fields: SpecFieldDefinition[] | undefined,
  flow: RfqFlow | undefined,
): SpecFieldDefinition[] {
  if (!fields) return [];
  if (!flow) return fields;
  return fields.filter(
    (field) => !field.showInFlows || field.showInFlows.includes(flow),
  );
}

export function isFieldRequiredForFlow(
  field: SpecFieldDefinition | undefined,
): boolean {
  if (!field) return false;
  return field.required ?? false;
}

function buildFieldConfig(
  productTypeSlug: string | null | undefined,
): SpecFieldConfig | null {
  if (!productTypeSlug || !isProductType(productTypeSlug)) return null;

  const rawFields = PRODUCT_TYPE_FIELDS[productTypeSlug];
  if (!rawFields) return null;

  const fields: SpecFieldDefinition[] = rawFields.map((field) => ({
    ...field,
    type: toSpecFieldType(field.type),
    required: field.required ?? false,
    options: field.options?.map((option) => ({ label: option.label, value: option.value })),
    showWhen: field.showWhen,
    showInFlows: field.showInFlows ? [...field.showInFlows] : undefined,
  }));

  const conditions = PRODUCT_TYPE_CONDITIONS[productTypeSlug];

  return {
    fields,
    flowOptions: { conditions: conditions ? [...conditions] : undefined },
  };
}

export function useFieldConfig(
  productTypeSlug: string | null | undefined,
  flow?: RfqFlow,
): UseFieldConfigResult {
  const fieldConfig = useMemo(
    () => buildFieldConfig(productTypeSlug),
    [productTypeSlug],
  );

  const getField = (name: string): SpecFieldDefinition | undefined => {
    return fieldConfig?.fields?.find((field) => field.name === name);
  };

  const getRequiredFields = (): SpecFieldDefinition[] => {
    return fieldConfig?.fields?.filter((field) => field.required) ?? [];
  };

  const getFieldsForFlow = () => fieldsForFlow(fieldConfig?.fields, flow);

  const getConditionOptions = (): Array<{ label: string; value: string }> => {
    return fieldConfig?.flowOptions?.conditions ?? [];
  };

  const checkFieldRequiredForFlow = (fieldName: string): boolean => {
    const field = getField(fieldName);
    return isFieldRequiredForFlow(field);
  };

  return {
    fieldConfig,
    isLoading: false,
    getField,
    getRequiredFields,
    getFieldsForFlow,
    getConditionOptions,
    isFieldRequiredForFlow: checkFieldRequiredForFlow,
  };
}
